import { petcoPewterBlue, petcoWhite } from '@common/_assets/color-constants.js';
import { ColorPickerPopover } from '@common/components/forms';
import { useApp } from '@common/hooks';
import { ClientConfigurationModel, HospitalHoursModel, ScheduleBlockTypeModel, schedule } from '@common/models';
import { EntityScheduleType, WeekDay } from '@common/models/Enums';
import { getCalendarEventItemId } from '@common/utils';
import FullCalendar, { EventContentArg, EventDropArg, EventInput } from '@fullcalendar/react';
import interactionPlugin, { Draggable, DropArg, EventReceiveArg, EventResizeDoneArg } from '@fullcalendar/interaction';
import { ResourceInput } from '@fullcalendar/resource-common';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import timeGridPlugin from "@fullcalendar/timegrid";
import { PetcoCalendarTheme } from '@hospital/components/calendar/PetcoCalendarTheme';
import { find, first, isEmpty, isEqual, xorWith } from "lodash";
import { DateTime } from 'luxon';
import { makeAutoObservable, observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import React, { useCallback } from 'react';
import { Col, Form, InputGroup, Row } from 'react-bootstrap';
import { v4 as uuid } from 'uuid';

//strongly typed EventInput extensions, deterministic unions
type SchedulePreferenceEvent = {
    preference: schedule.EntitySchedulePreference | undefined;
} & EventInput

const eventDate = "2000-01-01"
const dayOfWeek = WeekDay.fromDateTime(DateTime.fromISO(eventDate, { zone: 'UTC' }));
const defaultProfileColor = petcoPewterBlue;

const draggableScheduleBlockType = (sb: ScheduleBlockTypeModel, editing: boolean) =>
    <span key={sb.scheduleBlockTypeId}
        id={sb.scheduleBlockTypeId.toString()}
        role="listitem"
        title={sb.name}
        className='schedule-block'
        style={{
            color: sb.color,
            backgroundColor: `${sb.color}25`,
            cursor: editing ? "pointer" : "auto",
            border: editing ? `1px solid ${sb.color}` : `1px solid ${sb.color}05`
        }}>
        {sb.name}
    </span>

// Make schedule block elements draggable into the grid.
const initDraggableBlocks = () =>
    new Draggable(document.getElementById("schedule-block-list")!, {
        itemSelector: ".schedule-block",
        eventData: (eventEl: HTMLElement) => ({
            id: eventEl.getAttribute("data"),
            title: eventEl.getAttribute("title")
        })
    });
export class SchedulePreferencesUIStore {
    private _configuration: ClientConfigurationModel;
    private _schedulePreferencesType: EntityScheduleType;
    private _editing: boolean = false;
    private _originalScheduleColor: string = '';
    private _schedulingColor: string = ''; //used to compute whether color is dirty in hasChanges
    private _templateOrVetId: number;
    private _originalEvents = observable<SchedulePreferenceEvent>([]); //used to compute whether preferences are dirty in hasChanges
    private _hospitalHours = observable<HospitalHoursModel>([]);
    events = observable<SchedulePreferenceEvent>([]);

    constructor(init: { configuration: ClientConfigurationModel, schedulePreferencesType: EntityScheduleType, templateOrVetId: number, schedulingColor: string, hospitalHours: HospitalHoursModel[] | undefined }) {
        this._configuration = init.configuration;
        this._schedulePreferencesType = init.schedulePreferencesType;
        this._templateOrVetId = init.templateOrVetId;
        this._originalScheduleColor = init.schedulingColor;
        this._schedulingColor = init.schedulingColor;
        if (init.hospitalHours) {
            this._hospitalHours.clear();
            init.hospitalHours.forEach(e => this._hospitalHours.push(e));
        }
        makeAutoObservable(this, undefined, { autoBind: true });
    }

    get editing() { return this._editing; }
    set editing(value: boolean) { this._editing = value; }

    get schedulingColor() { return this._schedulingColor; }
    set schedulingColor(value: string) { this._schedulingColor = value; }

    get eventSchedulePreferences(): schedule.EntitySchedulePreference[] {
        const ret = this.events.map(e => (e as SchedulePreferenceEvent).preference).filter(a => a !== undefined);
        return ret as schedule.EntitySchedulePreference[] ?? [];
    }

    get resources() {
        const ret = [0, 1, 2, 3, 4, 5, 6].map(i => {
            const dayName = WeekDay.getLongName(i);
            const hospitalHour: HospitalHoursModel | undefined = this._hospitalHours.find(h => h.dayOfWeek === i);
            const resource: ResourceInput = {
                id: i.toString(),
                title: dayName,
                businessHours: hospitalHour ? {
                    id: hospitalHour.hospitalHoursId.toString(),
                    daysOfWeek: [dayOfWeek],
                    startTime: hospitalHour.isOpen ? hospitalHour.openTime : `00:00:00`,
                    endTime: hospitalHour.isOpen ? hospitalHour.closeTime : `00:00:00`,
                    overlap: false,
                    interactive: false
                } : undefined
            }
            return resource;
        });
        return ret;
    }

    get minOpenTime() {
        const sorted = this._hospitalHours.map(h => h.openTime).sort();
        return sorted.length > 0 ? sorted[0] : '07:00:00';
    }

    get maxCloseTime() {
        const sorted = this._hospitalHours.map(h => h.closeTime).sort();
        return sorted.length > 0 ? sorted[sorted.length - 1] : '19:00:00'
    }

    /**
     * Returns true if any initially loaded events have changed, been added, deleted, or the scheduling color has changed since loaded.
     * Essentially this is dirty tracking on the schedule preferences and scheduling color.
     */
    get hasChanges() {
        if (this._originalScheduleColor !== this._schedulingColor)
            return true;
        const origEvents = toJS(this._originalEvents);
        const currentEvents = toJS(this.events);
        const eventsHaveChanged = !isEmpty(xorWith(origEvents, currentEvents, isEqual));
        return eventsHaveChanged;
    }

    /**
     * Clears any existing events and creates new events  from all the schedule preferences passed in, you should pass in hosp or vet based on the SchedulePreferencesType the store was instantiated with, not both.
     * @param preferences A Hospital or Vet schedule prefrenceModel
    */
    setPreferences(preferences: schedule.EntitySchedulePreference[]) {
        this.events.clear();
        this._originalEvents.clear();
        preferences.map(this.preferenceToEvent).forEach(e => this.events.push(e));
        preferences.map(this.preferenceToEvent).forEach(e => this._originalEvents.push(e));
    }

    // modify the start/end time of the event
    updateEventStartEnd = (event: SchedulePreferenceEvent, start: string, end?: string) => {
        event.start = start;
        event.end = end ? end : DateTime.fromISO(start, { zone: 'UTC' }).plus({ hours: 1 }).toISO();

        const startTime = DateTime.fromISO(event.start, { zone: 'UTC' }).toISOTime({ includeOffset: false, suppressMilliseconds: true });
        const endTime = DateTime.fromISO(event.end, { zone: 'UTC' }).toISOTime({ includeOffset: false, suppressMilliseconds: true });

        if (event.preference) {
            event.preference.startTime = startTime;
            event.preference.endTime = endTime;
        }

        return event;
    }

    // modify the resource (day) of the event.
    updateEventResource = (event: SchedulePreferenceEvent, resourceId?: string) => {
        event.resourceId = resourceId ?? event.resourceId;
        if (event.preference && event.resourceId)
            event.preference.dayOfWeek = parseInt(event.resourceId);
        return event;
    }

    /**
     * Instantiates a schedule preference event for the calendar from a block type and pref model
     * @param sb the schedule block type to create an event for
     * @param model the hos pref or vet pref model to create an event for
     * @returns returns a SchedulePreferenceEvent
     */
    newEvent = (model: schedule.EntitySchedulePreference): SchedulePreferenceEvent => {
        var baseEntityPreferenceId = getCalendarEventItemId(model.uniqueId);

        let event: SchedulePreferenceEvent = {
            schedulePreferencesType: this._schedulePreferencesType,
            title: model.scheduleBlockTypeName,
            textColor: model.scheduleBlockTypeColor,
            backgroundColor: `${model.scheduleBlockTypeColor}25`, //25 makes it 25% opacity, important or text will be invisible
            borderColor: petcoWhite,
            preference: model,
            id: baseEntityPreferenceId === 0 ? `0_${uuid()}` : model.uniqueId,
        };
        return event;
    }


    /**
     * Converts a single preference to a schedule preference event
     * @param p hosp pref or vet pref depending on schedule preferences type
     * @returns a SchedulePreferenceEvent
     */
    preferenceToEvent(p: schedule.EntitySchedulePreference) {
        const event = this.newEvent(p);
        // convert time to date: 08:00:00 -> 2000-01-01T08:00:00
        this.updateEventStartEnd(event, `${eventDate}T${p.startTime}`, `${eventDate}T${p.endTime}`);
        this.updateEventResource(event, p.dayOfWeek.valueOf().toString());
        return event;
    }


    resizeEvent(arg: EventResizeDoneArg) {
        const event = find(this.events, e => e.id === arg.event.id);
        if (event)
            this.updateEventStartEnd(event, arg.event.startStr, arg.event.endStr);
    }

    moveEvent(arg: EventDropArg) {
        const event = find(this.events, { id: arg.event.id })!;
        this.updateEventStartEnd(event, arg.event.startStr, arg.event.endStr);
        this.updateEventResource(event, arg.newResource?.id);

    }

    /**
     * A schedule block was dragged into the grid. Create a corresponding event record. not to be confused with newEvent which merely instantiates an event object, this actually inserts the created event into the events.
     * @param arg
     */

    createEvent(arg: DropArg) {
        const schedBlock = find(this._configuration.scheduleBlockTypes, { name: arg.draggedEl.title })!;
        let event: SchedulePreferenceEvent;

        const newPreference = schedule.EntitySchedulePreference.createNewEntitySchedulePreference({
            scheduleBlockTypeId: schedBlock.scheduleBlockTypeId,
            scheduleBlockTypeName: schedBlock.name,
            scheduleBlockIsOverrideType: false,
            scheduleBlockTypeColor: schedBlock.color,
            rowVersion: '',
            entityType: this._schedulePreferencesType
        });


        event = this.newEvent(newPreference);

        this.updateEventStartEnd(event, arg.dateStr);
        this.updateEventResource(event, arg.resource?.id);
        this.events.push(event);
    }

    clearEvents() {
        this.events.clear();
    }

    // Remove the selected event from state
    deleteEvent(eventId: string) {
        const event = find(this.events, e => e.id === eventId);
        if (event)
            this.events.remove(event);
    }
}

interface IProps {
    calendarKey: string,
    className?: string,
    editing: boolean,
    hideActions?: boolean
    schedulingColor: string,
    schedulingName: string,
    templateOrVetId: number,
    schedulePreferencesType: EntityScheduleType,
    schedulePreferences: schedule.EntitySchedulePreference[];
    hospitalHours?: HospitalHoursModel[] | undefined,
    setPreferencesStore: (store: SchedulePreferencesUIStore) => void;
}

export const SchedulingPreferences = observer(({ hospitalHours, setPreferencesStore, schedulePreferencesType, schedulingName, schedulePreferences, schedulingColor, templateOrVetId, editing, className, calendarKey }: IProps) => {
    const fcRef = React.useRef<FullCalendar | null>(null); //used to know when FC has been rendered so it can be initialized in edit mode at load.
    const draggable = React.useRef<Draggable | undefined>(undefined);
    const { configuration } = useApp();
    //get the store, memo prevents it from being recreated unless the props actually change, this preserves state in the store
    const [uiStore] = React.useMemo(() => {
        return [new SchedulePreferencesUIStore({ configuration, schedulePreferencesType, templateOrVetId: templateOrVetId, schedulingColor: schedulingColor, hospitalHours })];
    }, [configuration, schedulePreferencesType, templateOrVetId, schedulingColor, hospitalHours]);

    // Enable schedule block dragging/dropping
    const enableEditing = useCallback(() => {
        draggable.current?.destroy();
        draggable.current = initDraggableBlocks();
        uiStore.editing = true;
    }, [uiStore]);

    // Disable schedule block dragging/dropping
    const disableEditing = useCallback(() => {
        draggable.current?.destroy();
        uiStore.editing = false;
    }, [uiStore]);

    //loading existing schedule preferences
    React.useEffect(() => {
        uiStore.setPreferences(schedulePreferences);
    }, [uiStore, schedulePreferences]);

    React.useEffect(() => {
        setPreferencesStore(uiStore);
    }, [setPreferencesStore, uiStore]);


    //enables editing based on editing prop once full calendar has rendered, can't create draggable until FC exists.
    React.useEffect(() => {
        if (fcRef && fcRef.current) {
            if (editing)
                enableEditing();
            else
                disableEditing();
        }
    }, [fcRef, editing, disableEditing, enableEditing]);

    // generate the markup for the grid event
    const renderEvent = (arg: EventContentArg) => {
        const resource = first(arg.event.getResources());
        return (
            <div data-testid={`event-${arg.event.id}`}>
                <div className="d-flex flex-column">
                    <div className="schedule-block__event-title fw-bold">{arg.event.title}</div>
                    <div className="schedule-block__event-time">{arg.timeText}</div>
                </div>
                {uiStore.editing && resource &&
                    <div role="button" className="schedule-block__delete"
                        aria-label={`Delete ${WeekDay.getLongName(parseInt(resource.id))} ${arg.timeText} ${arg.event.title}`}
                        onClick={() => uiStore.deleteEvent(arg.event.id)}>
                        <i className="fa fa-trash-o" aria-hidden="true"></i>
                    </div>
                }
            </div>
        )
    }

    /**
     * When you drag a schedule block onto the grid, FullCalendar adds a stub event to the grid and
     * then fires 'eventReceive'. It fires 'drop' at the same time. Only 'drop' carries the necessary
     * metadata to properly create an event descriptor. (In particular, the 'eventReceive' metadata
     * is missing the event day, or "resource".)
     * Since the 'drop' args are sufficient to create the event, we don't need FullCalendar's stub event.
     * This handler simply tells FullCalendar to revert the addition of that stub event to the grid.
     */
    const receiveEvent = (arg: EventReceiveArg) => { arg.revert() };

    return (<>
        <Row className={className}>
            <Form.Label>{schedulePreferencesType === EntityScheduleType.Template ? 'Schedule Color' : 'Profile Color'}</Form.Label>
            <InputGroup>
                <div className="d-flex flex-fill flex-row">
                    <div>
                        {uiStore.editing &&
                            <ColorPickerPopover color={uiStore.schedulingColor} onChange={(newColor) => {
                                uiStore.schedulingColor = newColor;
                            }} />
                        }
                    </div>
                    <div>
                        <span className='schedule-block'
                            style={{
                                color: petcoWhite,
                                backgroundColor: uiStore.schedulingColor ? uiStore.schedulingColor : defaultProfileColor,
                                cursor: "auto",
                                border: `1px solid 00195205`
                            }}>
                            {schedulingName == null || schedulingName == '' ? 'Not Yet Named' : schedulingName}
                        </span>
                    </div>
                </div>
            </InputGroup>

        </Row>
        <Row className="mt-3">
            <Col md={12}>
                <div id="schedule-block-list" className="schedule-block-list d-flex flex-row mt-2 p-1">
                    <div>Available Blocks:</div>
                    <div role="list">
                        {configuration.scheduleBlockTypes.filter(e => e.isOverrideType === false).map(sb => draggableScheduleBlockType(sb, uiStore.editing))}
                    </div>
                </div>
            </Col>
        </Row>
        <Row>
            <FullCalendar
                key={calendarKey}
                ref={fcRef}
                themeSystem='petco'
                timeZone='utc'
                initialDate={eventDate}
                initialView='resourceTimeGridDay'
                plugins={[PetcoCalendarTheme.createPlugin(), resourceTimeGridPlugin, interactionPlugin, timeGridPlugin]}
                resources={toJS(uiStore.resources)}
                events={toJS(uiStore.events)}
                eventContent={renderEvent}
                eventOverlap={false}
                eventResizableFromStart={true}
                editable={uiStore.editing}
                droppable={uiStore.editing}
                allDaySlot={false}
                height='auto'
                headerToolbar={{ start: '', center: '', right: '' }}
                slotMinTime={uiStore.minOpenTime}
                slotMaxTime={uiStore.maxCloseTime}
                defaultTimedEventDuration="01:00:00"
                slotDuration="00:30:000"
                slotLabelInterval="00:30:000"
                slotLabelFormat={{ hour: 'numeric', minute: '2-digit', }}
                slotLabelClassNames={"fc-dvm-schedule-preference-slot"}
                slotLaneClassNames={"fc-dvm-schedule-preference-slot"}
                eventClassNames={"fc-dvm-schedule-preference-event"}
                eventDrop={uiStore.moveEvent}
                eventResize={uiStore.resizeEvent}
                eventReceive={receiveEvent}
                drop={uiStore.createEvent}
                businessHours={false}
                selectConstraint="businessHours"
                eventConstraint="businessHours"
            />
        </Row>
        <Row className="mt-3">
            <div className="d-flex gap-2 note">
                <div><i className="fa fa-circle schedule-block__appointments" aria-hidden="true"></i></div>
                <div>Online booking only available for appointments.</div>
            </div>
        </Row>
    </>)
});
